// src/review/steps/ReviewSummary.jsx
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Stack, Card, Text, Button, Textarea, Group, Alert, Badge } from '@mantine/core';
import { IconCircleCheck, IconAlertCircle } from '@tabler/icons-react';
import { useReview } from '../context/ReviewContext';
import { approveReview } from '../../redux/actions/reviewActions';

/**
 * ReviewSummary - Step 3 of review flow
 * Shows summary of changes and approval actions
 */
function ReviewSummary() {
  const dispatch = useDispatch();
  const { modelId, assessmentUuid, goToStep } = useReview();
  const [comments, setComments] = useState('');
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [error, setError] = useState(null);

  // Get review state
  const reviewState = useSelector((state) =>
    state.reviews.byModel[modelId] || {}
  );

  const edits = reviewState.edits || {};
  const totalEdits = Object.values(edits).reduce((total, category) => {
    return total + Object.keys(category).length;
  }, 0);

  // Handle approval
  const handleApprove = async () => {
    setApproving(true);
    setError(null);

    try {
      await dispatch(approveReview(modelId, assessmentUuid, comments));
      setApproved(true);
    } catch (err) {
      setError(err.message || 'Failed to approve review');
    } finally {
      setApproving(false);
    }
  };

  if (approved) {
    return (
      <Stack gap="lg" align="center" pt="xl">
        <IconCircleCheck size={64} color="#40c057" />
        <Text size="xl" fw={700} c="green">
          Review Approved Successfully
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          The assessment has been approved and the owner will be notified.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Card withBorder p="lg">
        <Stack gap="md">
          <Text size="xl" fw={700}>Review Summary</Text>
          <Text size="sm" c="dimmed">
            Review the changes made during this review session before approving.
          </Text>

          {/* Edit Summary */}
          <Card withBorder p="md" bg="gray.0">
            <Stack gap="xs">
              <Text size="md" fw={600}>Changes Made</Text>
              <Group gap="md">
                <Badge size="lg" variant="light" color="blue">
                  {Object.keys(edits.risks || {}).length} risk edits
                </Badge>
                <Badge size="lg" variant="light" color="orange">
                  {Object.keys(edits.controls || {}).length} control edits
                </Badge>
                <Badge size="lg" variant="light" color="green">
                  {Object.keys(edits.residualRisks || {}).length} residual risk edits
                </Badge>
                <Badge size="lg" variant="light" color="violet">
                  {Object.keys(edits.threats || {}).length} threat edits
                </Badge>
              </Group>
              <Text size="sm" c="dimmed" mt="xs">
                Total changes: {totalEdits}
              </Text>
            </Stack>
          </Card>

          {/* Review Comments */}
          <Textarea
            label="Review Comments (Optional)"
            placeholder="Add any comments or feedback about this assessment..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            minRows={4}
            maxRows={8}
          />

          {/* Error Alert */}
          {error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {error}
            </Alert>
          )}

          {/* Actions */}
          <Group justify="space-between" mt="lg">
            <Button
              variant="subtle"
              onClick={() => goToStep('security')}
            >
              ← Back to Security Review
            </Button>

            <Group gap="sm">
              <Button
                variant="outline"
                onClick={() => goToStep('risks')}
              >
                Back to Risks
              </Button>
              <Button
                color="green"
                size="lg"
                leftSection={<IconCircleCheck size={18} />}
                onClick={handleApprove}
                loading={approving}
              >
                Approve Assessment
              </Button>
            </Group>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

export default ReviewSummary;
